<template>
  <v-dialog v-model="showModal" fullscreen :max-width="maxWidth">
    <v-card flat class="mt-5">
      <v-container>
        <!-- Vuetify 2 ignored `size` on a button and drew this `fab` at its
             default 56px, with a 4em (56px) icon; v3 honours the prop. -->
        <v-btn
          class="ma-5 connect-contact__close"
          position="absolute"
          size="56"
          icon
          @click="closeEventHandler"
        >
          <v-icon size="56">mdi-close</v-icon>
        </v-btn>
        <v-card-text>
          <v-row class="pt-3 justify-center">
            <v-col cols="12">
              <ContactSelect
                :selected="selectedContacts"
                :visibility="visibility"
                :reset="reset"
                :query-by-role="queryByRole"
                :allow-selection="allowSelection"
                :title="title"
                is-force-touch
                @select="selectHandler"
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions
          v-if="$vuetify.display.xs"
          style="
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            width: 100%;
            background-color: #fff;
          "
        >
          <v-spacer />
          <v-btn color="primary" @click="connectContactHandler">
            {{ buttonText }}
          </v-btn>
          <v-btn variant="text" @click="closeEventHandler">Cancel</v-btn>
        </v-card-actions>
      </v-container>
    </v-card>
    <!-- Shown while there is a selection; dismissing it clears the selection,
         which is what writing `false` into the array's length used to do. -->
    <v-snackbar
      :model-value="selectedContacts.length > 0"
      class="connect-contact-snackbar"
      location="end"
      max-width="300px"
      :timeout="-1"
      @update:model-value="(shown) => { if (!shown) selectedContacts = []; }"
    >
      <div>
        <div class="overline">{{ title.substring(0,3) === 'Add' ? title : `Add ${title}` }}</div>
        <v-card
          v-for="contact in selectedContacts"
          :key="contact._id"
          class="mb-4"
          flat
        >
          <ContactCard
            :item="contact"
            allow-remove
            :source="source"
            :contact-company="contactCompany"
            :use-company="useCompany"
            :show-photo="false"
            show-internal
            is-flat
            @removeHandler="removeHandler(contact._id)"
            @updateContactCompany="setContactCompany"
          />
        </v-card>
        <div v-if="selectedContacts.length" class="text-right mt-2">
          <v-spacer />
          <v-btn variant="text" class="cancel" @click="closeEventHandler">
            Cancel
          </v-btn>
          <v-btn color="primary" @click="connectContactHandler">
            {{ buttonText }}
          </v-btn>
        </div>
      </div>
    </v-snackbar>
  </v-dialog>
</template>

<script>
import ContactCard from '@/components/common-components/recordCards/ContactCard'

import ContactSelect from './ContactSelect'

export default {
  name: 'ConnectContact',

  components: {
    ContactSelect,
    ContactCard,
  },

  props: {
    modelValue: Boolean,
    closable: Boolean,
    source: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      default: 'Select Contact',
    },
    displayTitle: {
      type: Boolean,
      default: true,
    },
    flat: Boolean,
    contacts: {
      type: Array,
      default: () => [],
    },
    transactionType: {
      type: String,
      default: '',
    },
    visibility: {
      type: [Boolean, Number],
      default: false,
    },
    role: {
      type: String,
      default: '',
    },
    buttonText: {
      type: String,
      default: 'Add Contacts',
    },
    reset: {
      type: Number,
      default: 0,
    },
    queryByRole: {
      type: String,
      default: '',
    },
    useCompany: Boolean,
    allowMultiple: {
      type: Boolean,
      default: true,
    },
    maxWidth: {
      type: String,
      default: '100%'
    }
  },

  emits: ['close', 'connect', 'update:modelValue'],

  data: () => ({
    sourceInstance: null,
    selectedContacts: [],
    contactCompany: null,
  }),

  computed: {
    allowSelection () {
      return !(!this.allowMultiple && this.selectedContacts.length)
    },
    showModal: {
      get: function () {
        return this.modelValue
      },
      set: function (value) {
        this.$emit('update:modelValue', value)
      }
    }
  },

  watch: {
    contacts: function (value) {
      this.contactsChangeHandler(value)
    },
    visibility: function (value) {
      if (!value) {
        this.selectedContacts = []
      }
    },
  },

  methods: {
    setContactCompany (payload) {
      this.contactCompany = payload
    },
    /**
     * Handler for select contact event
     * @param {Array} items
     * */
    selectHandler: function (items) {
      this.contactCompany = items.length === 1 ? items[0]._id : this.contactCompany
      this.selectedContacts = items.map((item) => {
        item.role = ''
        return item
      })
    },
    /**
     * Handler for remove event
     * @param {string} id
     * */
    removeHandler: function (id) {
      this.selectedContacts = this.selectedContacts.filter(
        (item) => item._id !== id
      )
    },
    /**
     * Handler for connect contact event
     * */
    connectContactHandler () {
      const connectedContacts = this.selectedContacts.map((contact) => {
        contact.roles = this.role ? [this.role] : contact.roles
        contact.useCompany = contact._id === this.contactCompany
        return contact
      })
      this.$emit('connect', connectedContacts, true)
      this.selectedContacts = []
    },
    /**
     * Handler for contacts change event
     * @param {Array} value
     * */
    contactsChangeHandler: function (value) {
      if (value.length) {
        this.selectedContacts = [
          ...this.selectedContacts,
          ...value.filter(
            (item) =>
              !this.selectedContacts.some(
                (contact) => contact._key === item._key
              )
          ),
        ]
      }
    },
    /**
     * Handler for close event
     * */
    closeEventHandler: function () {
      this.$emit('close')
    },
  },
}
</script>

<style scoped>
/* Vuetify 2's `right` on an absolute button set `right: 16px` and left the
   vertical offset to the button's static position. v3 has no such prop, and
   `location="right"` would also centre it vertically, so set the inset here. */
.connect-contact__close {
  right: 16px;
}
</style>
